/**
 * PSV-1728 — crystals as physical cargo + asteroid split/contact physics.
 *
 * Balance + helper module shared by both sims. The SERVER is the only
 * authority: it spawns bursts, simulates magnet pull, credits pickups
 * (bounded by the class cargo hold) and expires strays. The CLIENT
 * mirrors the ballistic part (scatter + friction) locally from the
 * `CrystalBurst` broadcast so crystal motion costs zero wire traffic;
 * `CrystalCollected` / `CrystalsExpired` are the authoritative endings.
 *
 * Wire events (group broadcasts, PSV-1337 asteroid pattern):
 *   CrystalBurst      { crystals: [{id,x,y,vx,vy,value,expiresAt}], t }
 *   CrystalCollected  { id, userId, value, gemsAfter, t }
 *   CrystalsExpired   { ids: string[], t }
 */

/** Uncollected crystals despawn after this long (data-plane hygiene). */
export const CRYSTAL_TTL_MS = 30_000;

/** Ships attract crystals inside this radius (px)… */
export const CRYSTAL_MAGNET_RADIUS_PX = 110;
/** …and absorb them inside this one. */
export const CRYSTAL_COLLECT_RADIUS_PX = 26;
/** Magnet acceleration toward the ship, px/s². */
export const CRYSTAL_MAGNET_ACCEL = 900;

/**
 * Scatter speed range for a fresh burst, px/s. Decays by
 * CRYSTAL_FRICTION_PER_S so crystals settle near the kill site instead
 * of sailing off (both sims integrate `v *= friction^dt`).
 */
export const CRYSTAL_SCATTER_SPEED_MIN = 60;
export const CRYSTAL_SCATTER_SPEED_MAX = 190;
export const CRYSTAL_FRICTION_PER_S = 0.12;

/** Server-side cap on simultaneously live crystals (oldest dropped). */
export const MAX_LIVE_CRYSTALS = 160;

/**
 * How many crystals a burst of `totalValue` gems shatters into: 2–6,
 * roughly one crystal per 4 gems. Death drops reuse the same curve so a
 * fat cargo spill reads as a fat spill.
 */
export function crystalCountForValue(totalValue: number): number {
  if (totalValue <= 0) return 0;
  return Math.max(2, Math.min(6, Math.ceil(totalValue / 4)));
}

/**
 * Split `totalValue` into `count` integer crystal values that sum
 * exactly (front-loaded remainder). count=0 → [].
 */
export function splitCrystalValues(totalValue: number, count: number): number[] {
  if (count <= 0 || totalValue <= 0) return [];
  const base = Math.floor(totalValue / count);
  const remainder = totalValue - base * count;
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0)).filter(
    (v) => v > 0,
  );
}

// ---------------------------------------------------------------------------
// Asteroid split (large → medium → small → crystals only)
// ---------------------------------------------------------------------------

/** Children spawned when a rock of the given tier shatters. */
export const ASTEROID_SPLIT_CHILDREN: Readonly<
  Record<'large' | 'medium' | 'small', { child: 'medium' | 'small' | null; min: number; max: number }>
> = {
  large: { child: 'medium', min: 2, max: 3 },
  medium: { child: 'small', min: 2, max: 3 },
  small: { child: null, min: 0, max: 0 },
} as const;

/** Fragment fling speed range, px/s (faster than ambient drift ≤30). */
export const FRAGMENT_SPEED_MIN = 40;
export const FRAGMENT_SPEED_MAX = 90;

// ---------------------------------------------------------------------------
// Ship ↔ asteroid contact damage + bounce
// ---------------------------------------------------------------------------

/** Damage per px/s of relative closing speed. */
export const CONTACT_DAMAGE_PER_SPEED = 0.08;
/** Floor so even a slow scrape stings a little. */
export const CONTACT_DAMAGE_MIN = 3;
/** Per-ship invulnerability window between contact hits, ms. */
export const CONTACT_DAMAGE_COOLDOWN_MS = 600;
/** Velocity restitution for the elastic bounce (1 = perfectly elastic). */
export const CONTACT_BOUNCE_RESTITUTION = 0.7;
/** Extra positional separation shove, px (prevents re-overlap next tick). */
export const CONTACT_SEPARATION_PX = 2;

/** Contact damage from a relative closing speed (px/s). */
export function contactDamage(relativeSpeed: number): number {
  return Math.max(CONTACT_DAMAGE_MIN, Math.round(relativeSpeed * CONTACT_DAMAGE_PER_SPEED));
}
